import {
  PlayerItemToPlay,
  MediaToPlay,
  PlayerMix,
  SegmentToPlay,
  ShowToPlay
} from '../models';
import { Injectable } from '@angular/core';
import { Uuid } from '@hidat/huijs-common';

/*
What do we want to be able to do?
- Skip to the next segment in the current show
- Know if the current show is done playing
- Queue up the next mixes
- Jump to the next show when the current segment is finished
- Jump to the next queued mix when the current segment is finished
- Jump to the next mix when the current mix is finished (but what is finished?)
- take a detour that stops playback of current show, plays a segment, and then returns to where you were

*/

// Some thoughts around creating an 'actions' queue
export enum PlaybackActions {
  PLAY_MIX_ALL,
  PLAY_MIX_HOUR,
  PLAY_TO_END_OF_HOUR,
  TAKE_DETOUR
}

export class PlaybackAction {
  action: PlaybackActions = PlaybackActions.PLAY_MIX_ALL;
  sourceId: Uuid | undefined;
}

/**
 * Helper class to the Playback  Service to keep track of queued mixes and segments.
 * Built to work with the steering wheel, and replaces the old scheduling class.
 */
@Injectable({ providedIn: 'root' })
export class PlaybackQueueService {

  // Any mixes that are queued to play next.  Does not include the current mix.  These have NOT been scheduled.
  protected mixQueue: PlayerMix [] = [];
  // What actions to take when the current media item is finished playing
  //protected nextQueueAction: SteeringActionToTake | undefined;

  // Schedulable Items Queue - either show or segments.  This is the users perspective of what is playing
  protected itemsToPlay: PlayerItemToPlay [] = [];
  protected currentItemToPlay = 0;

  // Media To Play Queue - the files that are being played
  protected mediaToPlay: MediaToPlay [] = [];
  protected currentMediaToPlay = 0;

  /**
   * Enqueues a mix to be played after the current one finishes.
   * @param mix - The mix to enqueue.
   * @param addToStart - Insert at the front of the queue instead of the back.
   */
  public addMixToQueue(mix: PlayerMix, addToStart = false) {
    if (addToStart) {
      this.mixQueue.unshift(mix);
    } else {
      this.mixQueue.push(mix);
    }
  }

  /** Returns `true` if at least one mix is waiting in the queue. */
  public hasMixQueued(): boolean {
    return this.mixQueue.length > 0;
  }

  /** Dequeues and returns the next mix, or `undefined` if the queue is empty. */
  public getNextMix(): PlayerMix | undefined {
    let mix: PlayerMix | undefined;
    if (this.mixQueue.length > 0) {
      mix = this.mixQueue.shift();
    }
    return mix;
  }

  protected addSegmentsToQueue(segmentToPlay: SegmentToPlay) {
    this.itemsToPlay.push(segmentToPlay);
    let firstInSegment: MediaToPlay | undefined;
    let lastInSegment: MediaToPlay | undefined;
    for (const mtp of segmentToPlay.mediaToPlay) {
      lastInSegment = mtp;
      if (!firstInSegment) {
        firstInSegment = mtp;
      }
      mtp.schedulingItem = segmentToPlay;
      mtp._segment = segmentToPlay.segment;
      mtp._host = segmentToPlay._host;
      mtp._show = segmentToPlay._show;
      this.mediaToPlay.push(mtp);
    }
    if (firstInSegment) {
      firstInSegment.firstInItem = true;
    }
    if (lastInSegment) {
      lastInSegment.lastInItem = true;
    }
  }

  protected addShowsToQueue(showToPlay: ShowToPlay) {
    this.itemsToPlay.push(showToPlay);
    let firstInShow: MediaToPlay | undefined;
    let lastInShow: MediaToPlay | undefined;
    for (const stp of showToPlay.segmentsToPlay) {
      for (const mtp of stp.mediaToPlay) {
        lastInShow = mtp;
        if (!firstInShow) {
          firstInShow = mtp;
        }
        mtp.schedulingItem = showToPlay;
        mtp._show = showToPlay.show;
        mtp._host = showToPlay._host;
        mtp._segment = stp.segment;
        this.mediaToPlay.push(mtp);
      }
    }
    if (firstInShow) {
      firstInShow.firstInItem = true;
    }
    if (lastInShow) {
      lastInShow.lastInItem = true;
    }
  }

  /**
   * Adds an array of generic ItemToPlay to the queue, figuring out what type of item each one is...
   * @param items
   */
  public addItemsToQueue(items: PlayerItemToPlay []) {
    for (const item of items) {
      if (item instanceof ShowToPlay) {
        this.addShowsToQueue(item);
      } else if (item instanceof SegmentToPlay) {
        this.addSegmentsToQueue(item);
      } else if (item instanceof MediaToPlay) {
        this.addMediaToPlay(item);
      }
    }
  }

  /**
   * Add given Media(s) to Play to the queue.
   * @param mediasToPlay
   */
  protected addMediaToPlay(mediasToPlay: MediaToPlay | MediaToPlay[]) {
    if (Array.isArray(mediasToPlay)) {
      for (const mtp of mediasToPlay) {
        this.mediaToPlay.push(mtp);
      }
    } else {
      this.mediaToPlay.push(mediasToPlay);
    }
  }

  /** Returns `true` if there are unconsumed media items remaining in the queue. */
  public hasMediaToPlay(): boolean {
    let results = false;
    if (this.mediaToPlay.length > this.currentMediaToPlay) {
      results = true;
    }
    return results;
  }

  /** Returns `true` when the queue is empty and the caller should fetch more content. */
  public needsMediaToPlay(): boolean {
    return !this.hasMediaToPlay();
  }

  /**
   * Advances the media cursor and returns the next `MediaToPlay`.
   * Returns `undefined` if the queue is exhausted — callers are responsible for fetching more.
   */
  getNextMediaItem(): MediaToPlay | undefined {
    let chosenItem: MediaToPlay | undefined = undefined;
    let done = false;
    while (this.currentMediaToPlay < this.mediaToPlay.length && !done) {
      chosenItem = this.mediaToPlay[this.currentMediaToPlay];
      this.currentMediaToPlay++;
      //if (this._playMicBreaks || chosenItem.mediaType !== 'MicBreak') {
      done = true;
    }
    return chosenItem;
  }

  /**
   * Repositions the media cursor to the given item so it plays next.
   * Used when the user clicks a specific track in the queue UI.
   * @param mediaItem - The item to seek to; matched by UID within its scheduling item.
   * @returns `true` if found and the cursor was moved; `false` otherwise.
   */
  gotoMediaItem(mediaItem: MediaToPlay): boolean {
    const miUid = mediaItem.uid;
    const siUid = mediaItem.schedulingItem?.uid;

    let index = 0;
    for(const mtp of this.mediaToPlay) {
      if (mtp.uid === miUid) {
        if (siUid === undefined || (mtp.schedulingItem && mtp.schedulingItem.uid === siUid)) {
          this.currentMediaToPlay = index;
          // Do we need to deal with scheduling item???
          return true;
        }
      }
      index++;
    }
    return false;
  }

  /** Returns `true` if there is at least one scheduling item after the current one. */
  public hasItemToPlay(): boolean {
    let results = false;
    if (this.itemsToPlay.length > (this.currentItemToPlay + 1)) {
      results = true;
    }
    return results;
  }

  /** Returns `true` when there are no more scheduling items and the caller should fetch more. */
  public needsItemToPlay(): boolean {
    return !this.hasItemToPlay();
  }

  /**
   * Advances to the next scheduling item and aligns the media cursor to its first file.
   * @returns The first `MediaToPlay` for the next item, or `undefined` if none available.
   */
  public getNextItemToPlay(): MediaToPlay | undefined {
    let mediaToPlay: MediaToPlay | undefined;
    if (this.hasItemToPlay()) {
      this.currentItemToPlay++;
      const itemToPlay = this.itemsToPlay[this.currentItemToPlay];

      // Find first media to play for this scheduling item
      for(let i=this.currentMediaToPlay; i < this.mediaToPlay.length; i++) {
        if (this.mediaToPlay[i].schedulingItem === itemToPlay) {
          this.currentMediaToPlay = i;
          mediaToPlay = this.mediaToPlay[this.currentMediaToPlay];
          break;
        }
      }
    }
    return mediaToPlay;
  }

  /**
   * Discards all not-yet-played items by advancing both cursors to the end of their queues.
   * Called before loading a new mix so stale content doesn't play after the switch.
   */
  public clearUnplayedItems() {
    this.currentItemToPlay = this.itemsToPlay.length;
    this.currentMediaToPlay = this.mediaToPlay.length;
  }
}
